import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  getGetAllowedOrganizationOfferGroupsQueryKey,
  useGetAllowedOrganizationOfferGroupsHook,
} from '@/api/trafikportalen';
import { offerGroupsSharedByOrganizations } from '@/helpers/sharedOrganizationOffers';
import type { CollectionTypeDTO } from '@/api/trafikportalen/models/collectionTypeDTO';

export const useOffersSharedByOrganizations = (
  organizationIds: string[],
  collectionType: CollectionTypeDTO,
  enabled: boolean,
) => {
  const getAllowedOfferGroups = useGetAllowedOrganizationOfferGroupsHook();
  const shouldFetch = enabled && organizationIds.length > 0;

  const queries = useQueries({
    queries: organizationIds.map((organizationId) => ({
      queryKey: getGetAllowedOrganizationOfferGroupsQueryKey(organizationId),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getAllowedOfferGroups(organizationId, signal),
      enabled: shouldFetch,
    })),
  });

  const isLoading = shouldFetch && queries.some((query) => query.isPending);
  const isError = shouldFetch && queries.some((query) => query.isError);

  const offers = useMemo(() => {
    if (!shouldFetch || isLoading || isError) return [];
    if (queries.some((query) => query.data === undefined)) return [];

    return offerGroupsSharedByOrganizations(
      queries.map((query) => query.data ?? []),
      collectionType,
    );
  }, [collectionType, isError, isLoading, queries, shouldFetch]);

  return { offers, isLoading, isError };
};
